import { USD_DECIMALS } from '@ui/constants/numbersDecimals';
import { getErc20TokenBySymbol } from '@ui/constants/tokens';
import { useFromInput } from '@ui/features/Trade/hooks/useFromInput';
import { useGetPayableTokenDecimals } from '@ui/features/Trade/hooks/useGetPayableTokenDecimals';
import { useLiqPriceAfterSpread } from '@ui/features/Trade/hooks/useLiqPriceAfterSpread';
import { useMarketPrice } from '@ui/features/Trade/hooks/useMarketPrice';
import { useStopLossStore } from '@ui/features/Trade/store/useStopLossStore';
import { useTakeProfitStore } from '@ui/features/Trade/store/useTakeProfitStore';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { OpenMarketTradeParams } from '@ui/features/Trade/types';
import { checkOpenMarketTradeParamsEq } from '@ui/features/Trade/utils/checkOpenMarketTradeParamsEq';
import { isSlInvalid } from '@ui/features/Trade/utils/validateStoppLossPrice';
import { useDataChainId } from '@ui/hooks/datachain';
import { toBigValue } from '@ui/lib/bignumber';
import { usePriceUpdatesByPosition } from '@ui/services/backend/hooks/oracle/usePriceUpdatesByPosition';
import { usePythUpdateFee } from '@ui/services/pyth-hermes/pyth-price-update/usePythUpdateFee';
import { Address } from '@ui/types/alias';
import { getOpenTradeQty } from '@ui/utils/trade/getOpenTradeQty';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';

import { usePayTokenPriceLatest } from '../../../hooks/usePayTokenPriceLatest';
import { usePositionSize } from '../../TradeModule/hooks/usePositionSize';
import { useCollateralToken } from '../../TradeModule/store/useCollateralToken';
import { useIsLong } from '../../TradeModule/store/useIsLong';
import { useIsLeverageMaxReached } from './useIsLeverageMaxReached';

export type OpenLimitOrderParams = OpenMarketTradeParams;
type OpenLimitOrderParamsStore = {
  params: OpenLimitOrderParams | undefined;
  setParams: (params: OpenLimitOrderParams | undefined) => void;
}
const useOpenLimitOrderParamsStore = create<OpenLimitOrderParamsStore>((set) => ({
  params: undefined,
  setParams: (params) => set({ params }),
}));

/** price is diff cal logic from open market trade, no need for slippage */
export const useOpenLimitOrderParamsUpdate = () => {
  const selectedTradingPair = useTradingPair((s) => s.useTradingPair());
  const isLong = useIsLong((s) => s._persistValue);
  const payToken = useCollateralToken((s) => s.useCollateralTokenValue());
  const payTokenPrice = usePayTokenPriceLatest(payToken);
  const payTokenDecimals = useGetPayableTokenDecimals();
  const inputAmount = useFromInput((s) => s.input).value.token;
  const inputUsd = useFromInput((s) => s.input).value.usd;
  const positionSize = usePositionSize({ inputUsd, isBeforeFee: false });
  const tpPriceValue = useTakeProfitStore((s) => s.tpValue).priceValue;
  const slPriceValue = useStopLossStore((s) => s.slValue).priceValue;
  const triggerPrice = useFromInput((s) => s.input).triggerPrice;
  const liqPrice = useLiqPriceAfterSpread();
  const marketPrice = useMarketPrice();
  const openLimitOrderParamsStore = useOpenLimitOrderParamsStore();
  const isLeverageReachMax = useIsLeverageMaxReached();

  const chainId = useDataChainId();
  const wrappedNativeToken = getErc20TokenBySymbol('WRAPPED_NATIVE', chainId);
  const collateralForOracle =
    payToken?.isNative && !isNil(wrappedNativeToken)
      ? wrappedNativeToken.address
      : payToken?.address;

  const { data: oracleBatchPrice } = usePriceUpdatesByPosition({
    pairBase: selectedTradingPair?.address,
    collateral: collateralForOracle,
    enabled: !!selectedTradingPair?.address && !!collateralForOracle,
  });
  const pythUpdateData = useMemo(
    () => (oracleBatchPrice?.pythPriceUpdateData ?? []) as Address[],
    [oracleBatchPrice?.pythPriceUpdateData],
  );
  const pythProUpdateData = useMemo(
    () => (oracleBatchPrice?.pythProPriceUpdateData ?? []) as Address[],
    [oracleBatchPrice?.pythProPriceUpdateData],
  );
  const oraclePythUpdateFee = oracleBatchPrice?.updateFee
    ? BigInt(oracleBatchPrice.updateFee)
    : undefined;
  const extraFee = oracleBatchPrice?.verifition_fee
    ? BigInt(oracleBatchPrice.verifition_fee)
    : 0n;

  const { data: fallbackPythUpdateFee } = usePythUpdateFee({
    pythUpdateData: pythUpdateData,
  });
  const pythUpdateFee = oraclePythUpdateFee ?? fallbackPythUpdateFee;

  useEffect(() => {
    if (
      isNil(inputAmount) ||
      inputAmount.isNaN() ||
      isNil(payTokenPrice) ||
      isNil(triggerPrice) ||
      isNaN(parseFloat(triggerPrice)) ||
      isNil(payToken) ||
      isNil(positionSize) ||
      positionSize.isNaN() ||
      isNil(selectedTradingPair) ||
      isNil(liqPrice) ||
      isNil(marketPrice) ||
      isNil(pythUpdateFee) ||
      isNil(oracleBatchPrice)
    )
      return;

    const entryPrice = new BigNumber(triggerPrice);

    const amountIn = toBigValue(inputAmount, payTokenDecimals); // should be before fee collateral token amount
    const tokenIn =
      payToken.isNative && !isNil(wrappedNativeToken)
        ? wrappedNativeToken.address
        : payToken.address;

    const newParams = {
      pairBase: selectedTradingPair.address,
      isLong: isLong,
      tokenIn,
      lvToken: payToken.isDirectCollateral ? tokenIn : payToken.lvTokenAddress,
      amountIn: amountIn,
      value: payToken.isNative
        ? amountIn + pythUpdateFee + extraFee
        : pythUpdateFee + extraFee,
      //pair amount, should be after fee
      qty: getOpenTradeQty({
        entryPrice,
        isLeverageReachMax,
        positionSize,
      }),
      /**
       * for limit order, this price should be exact the price user input,
       *
       * this pass in price is the limitPrice mentioned in doc, and SC will base on this limitPrice and slippageLongP/slippageShortP to calculate the real triggerPrice
       *
       */
      price: toBigValue(entryPrice, USD_DECIMALS),
      stopLoss:
        isNaN(parseFloat(slPriceValue)) ||
          isSlInvalid(liqPrice, slPriceValue, isLong, new BigNumber(marketPrice))
          ? 0n
          : toBigValue(slPriceValue, USD_DECIMALS),
      takeProfit: isNaN(parseFloat(tpPriceValue))
        ? 0n
        : toBigValue(tpPriceValue, USD_DECIMALS), // in biz logic, default TP should not be 0, but still give this handling for safety
      broker: 1,
      pythUpdateData: pythUpdateData as Address[],
      oracleUpdateData: {
        pythPriceUpdateData: pythUpdateData,
        pythProPriceUpdateData: pythProUpdateData,
      },
      extraFee,
    };

    if (checkOpenMarketTradeParamsEq(
      openLimitOrderParamsStore.params,
      newParams
    )) {
      return;
    }

    openLimitOrderParamsStore.setParams(newParams);
  }, [
    chainId,
    inputAmount,
    isLeverageReachMax,
    isLong,
    liqPrice,
    marketPrice,
    payToken,
    payTokenPrice,
    payTokenDecimals,
    positionSize,
    pythUpdateData,
    pythProUpdateData,
    pythUpdateFee,
    extraFee,
    oracleBatchPrice,
    openLimitOrderParamsStore,
    selectedTradingPair,
    slPriceValue,
    tpPriceValue,
    triggerPrice,
    wrappedNativeToken,
  ]);
};

export const useOpenLimitOrderParams = () => {
  return useOpenLimitOrderParamsStore((s) => s.params);
}